import {
	AfterViewInit,
	Component,
	CSP_NONCE,
	ElementRef,
	EventEmitter,
	inject,
	Input,
	OnChanges,
	OnDestroy,
	Output,
	SimpleChanges,
	ViewChild,
} from '@angular/core';

import { Compartment, EditorState, Extension } from '@codemirror/state';
import {
	Decoration,
	DecorationSet,
	EditorView,
	MatchDecorator,
	ViewPlugin,
	ViewUpdate,
} from '@codemirror/view';
import { basicSetup } from 'codemirror';

const getTokenClassName = (value: string): string => {
	if (value.startsWith('{{')) {
		return 'cm-template-variable';
	}

	if (value.startsWith('{%')) {
		return 'cm-template-control';
	}

	if (value.startsWith('#')) {
		return 'cm-markdown-heading';
	}

	return 'cm-markdown-strong';
};

const templateTokenMatcher = new MatchDecorator({
	decoration: (match) =>
		Decoration.mark({ class: getTokenClassName(match[0]) }),
	regexp: /{{[^}\n]*}}|{%[^%\n]*%}|^#{1,6}\s.+$|\*\*[^*\n]+\*\*/g,
});

const templateTokenHighlighter = ViewPlugin.fromClass(
	class {
		decorations: DecorationSet;

		constructor(view: EditorView) {
			this.decorations = templateTokenMatcher.createDeco(view);
		}

		update(update: ViewUpdate): void {
			this.decorations = templateTokenMatcher.updateDeco(
				update,
				this.decorations,
			);
		}
	},
	{ decorations: (plugin) => plugin.decorations },
);

const contractEditorTheme = EditorView.theme({
	'&': {
		backgroundColor: '#fff',
		color: '#212121',
		fontSize: '0.875rem',
		maxHeight: '70vh',
		minHeight: '20rem',
	},
	'&.cm-focused': { outline: 'none' },
	'.cm-activeLine': { backgroundColor: '#f5f7fa' },
	'.cm-activeLineGutter': { backgroundColor: '#eef1f5' },
	'.cm-content': {
		caretColor: '#3f51b5',
		lineHeight: '1.55',
		padding: '0.75rem 0',
	},
	'.cm-cursor': { borderLeftColor: '#3f51b5' },
	'.cm-gutters': {
		backgroundColor: '#fafafa',
		borderRight: '1px solid #e0e0e0',
		color: '#8a8a8a',
	},
	'.cm-line': { padding: '0 1rem' },
	'.cm-lineNumbers .cm-gutterElement': { padding: '0 0.75rem 0 0.5rem' },
	'.cm-markdown-heading': { color: '#1565c0', fontWeight: '700' },
	'.cm-markdown-strong': { color: '#6a1b9a', fontWeight: '600' },
	'.cm-scroller': {
		fontFamily: 'Consolas, "Courier New", monospace',
		overflow: 'auto',
	},
	'.cm-selectionBackground, ::selection': {
		backgroundColor: '#c5d8ff !important',
	},
	'.cm-template-control': { color: '#d84315', fontWeight: '600' },
	'.cm-template-variable': { color: '#9c6500', fontWeight: '600' },
});

@Component({
	selector: 'app-contract-code-editor',
	styleUrls: ['./contract-code-editor.component.scss'],
	templateUrl: './contract-code-editor.component.html',
})
export class ContractCodeEditorComponent
	implements AfterViewInit, OnChanges, OnDestroy
{
	@ViewChild('editorHost', { static: true })
	private readonly editorHost!: ElementRef<HTMLElement>;
	@Input() editable: boolean = false;
	@Input() value: string = '';
	@Output() readonly valueChange = new EventEmitter<string>();

	private readonly cspNonce = inject(CSP_NONCE);
	private readonly editableCompartment = new Compartment();
	private editor: EditorView | null = null;

	ngAfterViewInit(): void {
		this.editor = new EditorView({
			doc: this.value,
			extensions: [
				basicSetup,
				EditorView.cspNonce.of(this.cspNonce ?? ''),
				contractEditorTheme,
				templateTokenHighlighter,
				EditorView.lineWrapping,
				this.editableCompartment.of(this.getEditableExtensions()),
				EditorView.updateListener.of((update) => {
					if (this.editable && update.docChanged) {
						this.valueChange.emit(update.state.doc.toString());
					}
				}),
			],
			parent: this.editorHost.nativeElement,
		});
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!this.editor) {
			return;
		}

		if (changes.editable) {
			this.editor.dispatch({
				effects: this.editableCompartment.reconfigure(
					this.getEditableExtensions(),
				),
			});
		}

		if (changes.value && this.editor.state.doc.toString() !== this.value) {
			this.editor.dispatch({
				changes: {
					from: 0,
					insert: this.value,
					to: this.editor.state.doc.length,
				},
			});
		}
	}

	ngOnDestroy(): void {
		this.editor?.destroy();
		this.editor = null;
	}

	private getEditableExtensions(): Extension[] {
		return [
			EditorState.readOnly.of(!this.editable),
			EditorView.editable.of(this.editable),
		];
	}
}
